import React, { useEffect } from "react";
import {
  Activity,
  Bell,
  Building2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  LayoutDashboard,
  LogOut,
  Megaphone,
  Menu,
  Plug,
  Receipt,
  ScrollText,
  Settings,
  ShieldCheck,
  Tag,
  User,
  UserCog,
  Users,
  X,
} from "lucide-react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { useAtom, useAtomValue } from "jotai";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { AppLogo } from "@/components/ui/AppLogo";
import { BrandFooter } from "@/components/layout/BrandFooter";
import { ErrorBoundary } from "@/components/ui/ErrorBoundary";
import { AppErrorScreen } from "@/components/routing/AppErrorScreen";
import { AlertsBell } from "@/features/admin/components/AlertsBell";
import { AdminNavAlertsBadge } from "@/features/admin/components/AdminNavAlertsBadge";
import { CommandPaletteProvider } from "@/features/admin/command/CommandPaletteProvider";
import { CommandSearchInput } from "@/features/admin/command/CommandSearchInput";
import { DocsLink } from "@/components/layout/DocsLink";
import { cn } from "@/lib/cn";
import { useAuthStore } from "@/store/auth";
import {
  adminSidebarCollapsedAtom,
  sidebarMobileOpenAtom,
} from "@/store/sidebar";
import { pageHeaderAtom } from "@/store/ui";

function getUserInitials(
  firstName: string | undefined,
  lastName: string | undefined,
): string {
  const first = firstName?.[0]?.toUpperCase() ?? "";
  const last = lastName?.[0]?.toUpperCase() ?? "";
  return first + last || "?";
}

/**
 * AdminShell — layout for superadmin without impersonation.
 * Has its own sidebar with only admin nav items and its own header.
 * Dispatcher features (notifications, route pollers, banners) are NOT present here.
 */
export function AdminShell() {
  // A render error in one page must not stick after the user navigates away: the
  // boundary is keyed by the path, so a sidebar click mounts a fresh one (BUG-745).
  const { pathname } = useLocation();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const [mobileOpen, setMobileOpen] = useAtom(sidebarMobileOpenAtom);
  const [collapsed, setCollapsed] = useAtom(adminSidebarCollapsedAtom);
  // Breadcrumb trail set by each page via usePageHeader (BUG-153). The in-page
  // AdminPageHeader owns the visible H1; the shell header renders ONLY the
  // breadcrumb trail (de-emphasized context) so the title is never duplicated.
  // Falls back to the panel name as de-emphasized context before any page has
  // set its header.
  const { title: pageTitle, breadcrumbs } = useAtomValue(pageHeaderAtom);

  // Close the mobile drawer when the viewport grows to desktop (lg = 1024px),
  // mirroring the dispatcher AppShell/Sidebar behaviour.
  useEffect(() => {
    const BREAKPOINT = 1024;

    function handleResize() {
      if (window.innerWidth >= BREAKPOINT) {
        setMobileOpen(false);
      }
    }

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [setMobileOpen]);

  // Dismiss the mobile drawer on Escape for keyboard accessibility.
  useEffect(() => {
    if (!mobileOpen) return;

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setMobileOpen(false);
      }
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [mobileOpen, setMobileOpen]);

  const initials = getUserInitials(user?.first_name, user?.last_name);
  const displayName = user
    ? `${user.first_name} ${user.last_name}`.trim()
    : "User";

  async function handleSignOut() {
    // Await logout so any active impersonation session is ended (best-effort)
    // before navigating away and unmounting the shell.
    await logout();
    void navigate("/login");
  }

  function handleToggleLang() {
    const next = i18n.language === "en" ? "ru" : "en";
    void i18n.changeLanguage(next);
    localStorage.setItem("lang", next);
  }

  const langLabel = i18n.language === "ru" ? "RU" : "EN";

  return (
    <div className="flex h-screen overflow-hidden">
      {/* Global command palette (TASK-4221) — mounted once, opened via Cmd/Ctrl+K
          or the header trigger. Portalled, so its position here is irrelevant. */}
      <CommandPaletteProvider />

      {/* Mobile backdrop — shown when the drawer is open on small screens */}
      {mobileOpen && (
        <div
          aria-hidden="true"
          className="fixed inset-0 z-20 bg-black/50 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/*
        Left sidebar.
        Desktop (lg+): static in flow, fixed width w-56, always visible.
        Mobile (<lg): fixed overlay drawer that slides in/out via transform,
        toggled by the header hamburger (mirrors the dispatcher AppShell pattern).
      */}
      <aside
        className={cn(
          "flex flex-col bg-brand-sidebar [background-image:linear-gradient(180deg,hsl(var(--brand-sidebar))_0%,hsl(var(--brand-sidebar)/0.85)_100%)]",
          "fixed inset-y-0 left-0 z-30 transition-[transform,width] duration-200",
          "lg:relative lg:z-auto lg:shrink-0 lg:translate-x-0",
          mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0",
          collapsed ? "w-14 lg:w-14" : "w-56 lg:w-56",
        )}
      >
        {/* Logo area */}
        <div className="flex h-14 shrink-0 items-center justify-between border-b border-white/10 px-4">
          {!collapsed && (
            <AppLogo
              variant="dark"
              wordmark
              className="h-7 w-auto max-w-[150px]"
            />
          )}
          {/* Close button — mobile only */}
          <button
            type="button"
            aria-label={t("adminNav.closeMenu")}
            onClick={() => setMobileOpen(false)}
            className="flex items-center justify-center rounded-lg p-1.5 text-white/60 transition-colors hover:bg-white/10 hover:text-white lg:hidden"
          >
            <X size={18} />
          </button>
        </div>

        {/* Nav items */}
        <nav className="flex flex-1 flex-col gap-1 overflow-y-auto px-2 py-3">
          {(
            [
              {
                to: "/admin/dashboard",
                icon: LayoutDashboard,
                label: t("adminNav.dashboard"),
              },
              {
                to: "/admin/tenants",
                icon: Building2,
                label: t("adminNav.tenants"),
              },
              {
                to: "/admin/dealers",
                icon: UserCog,
                label: t("adminNav.dealers"),
              },
              {
                to: "/admin/dealer-domains",
                icon: ShieldCheck,
                label: t("adminNav.dealerDomains"),
              },
              { to: "/admin/users", icon: Users, label: t("adminNav.users") },
              {
                to: "/admin/billing",
                icon: Receipt,
                label: t("adminNav.billing"),
              },
              {
                to: "/admin/plans",
                icon: Tag,
                label: t("adminNav.plans"),
              },
              {
                to: "/admin/alerts",
                icon: Bell,
                label: t("adminNav.alerts"),
                badgeKey: "adminNav.alertsBadge",
              },
              {
                to: "/admin/activity-log",
                icon: ScrollText,
                label: t("adminNav.activityLog"),
              },
              {
                to: "/admin/integration",
                icon: Plug,
                label: t("adminNav.integrations"),
              },
              {
                to: "/admin/broadcast",
                icon: Megaphone,
                label: t("adminNav.broadcast"),
              },
              {
                to: "/admin/settings",
                icon: Settings,
                label: t("adminNav.settings"),
              },
              {
                to: "/admin/system-status",
                icon: Activity,
                label: t("adminNav.systemStatus"),
              },
            ] as {
              to: string;
              icon: React.ComponentType<{ size?: number; className?: string }>;
              label: string;
              badgeKey?: string;
            }[]
          ).map(({ to, icon: Icon, label, badgeKey }) => (
            <NavLink
              key={to}
              to={to}
              onClick={() => setMobileOpen(false)}
              title={collapsed ? label : undefined}
              className={({ isActive }) =>
                cn(
                  "relative flex items-center rounded-lg py-2 text-white/50 transition-all duration-150 hover:bg-white/8 hover:text-white/90",
                  collapsed ? "justify-center px-2" : "gap-3 px-3",
                  isActive && "bg-white/[0.12] text-white",
                )
              }
            >
              {({ isActive }) => (
                <>
                  {isActive && (
                    <span className="absolute left-0 top-1/2 h-5 w-0.5 -translate-y-1/2 rounded-r-full bg-brand-primary" />
                  )}
                  <Icon
                    size={18}
                    className={cn(
                      "shrink-0 transition-colors",
                      isActive ? "text-brand-primary" : "text-white/50",
                    )}
                  />
                  {!collapsed && (
                    <span
                      className={cn(
                        "truncate text-sm",
                        isActive
                          ? "font-semibold text-white"
                          : "font-medium text-white/70",
                      )}
                    >
                      {label}
                    </span>
                  )}
                  {/* Unread-alerts badge on the Alerts item (TASK-4222). */}
                  {badgeKey && (
                    <AdminNavAlertsBadge
                      labelKey={badgeKey}
                      collapsed={collapsed}
                    />
                  )}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        {/* Collapse toggle — desktop only */}
        <div className="hidden shrink-0 border-t border-white/10 px-2 py-2 lg:block">
          <button
            type="button"
            onClick={() => setCollapsed((v) => !v)}
            className={cn(
              "flex w-full items-center rounded-lg px-2 py-2 text-white/50 transition-colors hover:bg-white/10 hover:text-white",
              collapsed ? "justify-center" : "gap-2",
            )}
            aria-label={collapsed ? t("nav.expand") : t("nav.collapse")}
          >
            {collapsed ? (
              <ChevronRight size={16} />
            ) : (
              <>
                <ChevronLeft size={16} />
                <span className="text-xs">{t("nav.collapse")}</span>
              </>
            )}
          </button>
        </div>

        {/* User avatar + dropdown — bottom of sidebar */}
        <div className="shrink-0 border-t border-white/10 px-2 py-3">
          <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild>
              <button
                type="button"
                className={cn(
                  "flex w-full items-center rounded-lg px-2 py-2 text-left text-white/80 transition-colors hover:bg-white/5",
                  collapsed ? "justify-center" : "gap-2",
                )}
              >
                {user?.avatar_url ? (
                  <img
                    src={user.avatar_url}
                    alt={displayName}
                    className="h-8 w-8 shrink-0 rounded-full object-cover"
                  />
                ) : (
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/20 text-xs font-semibold text-white">
                    {initials}
                  </span>
                )}
                {!collapsed && (
                  <span className="flex min-w-0 flex-1 items-center gap-1">
                    <span className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-white">
                        {displayName}
                      </p>
                      <p className="truncate text-xs text-white/50">
                        {user?.email ?? ""}
                      </p>
                    </span>
                    <ChevronDown size={14} className="shrink-0 text-white/40" />
                  </span>
                )}
              </button>
            </DropdownMenu.Trigger>

            <DropdownMenu.Portal>
              <DropdownMenu.Content
                side="top"
                align="start"
                sideOffset={4}
                className="z-50 min-w-[200px] rounded-lg border border-gray-200 bg-white p-1 shadow-lg"
              >
                {/* Non-clickable user info header */}
                <div className="px-3 py-2">
                  <p className="text-xs text-gray-500">{user?.email ?? ""}</p>
                </div>

                <DropdownMenu.Separator className="my-1 h-px bg-gray-100" />

                <DropdownMenu.Item
                  onSelect={() => navigate("/admin/profile")}
                  className="flex cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-sm text-gray-700 outline-none hover:bg-gray-50 focus:bg-gray-50"
                >
                  <User size={14} />
                  {t("nav.profile")}
                </DropdownMenu.Item>

                <DropdownMenu.Separator className="my-1 h-px bg-gray-100" />

                <DropdownMenu.Item
                  onSelect={() => void handleSignOut()}
                  className="flex cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-sm text-gray-700 outline-none hover:bg-gray-50 focus:bg-gray-50"
                >
                  <LogOut size={14} />
                  {t("nav.signOut")}
                </DropdownMenu.Item>
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>
        </div>
      </aside>

      {/* Right column: header + main content */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Header */}
        <header className="flex h-14 shrink-0 items-center justify-between border-b border-gray-200 bg-white px-4 lg:px-6">
          {/* Left: hamburger (mobile only) + title */}
          <div className="flex items-center gap-3">
            <button
              type="button"
              aria-label={t("adminNav.openMenu")}
              aria-expanded={mobileOpen}
              onClick={() => setMobileOpen(true)}
              className="flex items-center justify-center rounded-lg p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900 lg:hidden"
            >
              <Menu size={20} />
            </button>
            {/* Breadcrumb trail only — the in-page AdminPageHeader owns the H1
                (no duplicate title, BUG-153). Falls back to the panel name as
                de-emphasized context when a page sets no breadcrumbs. */}
            {breadcrumbs && breadcrumbs.length > 0 ? (
              <nav
                aria-label="Breadcrumb"
                className="flex items-center gap-1 overflow-hidden"
              >
                {breadcrumbs.map((crumb, index) => (
                  <span key={index} className="flex items-center gap-1">
                    {index > 0 && (
                      <ChevronRight size={12} className="text-gray-300" />
                    )}
                    {crumb.to ? (
                      <Link
                        to={crumb.to}
                        className="truncate text-xs font-medium text-gray-400 hover:text-gray-600"
                      >
                        {crumb.label}
                      </Link>
                    ) : (
                      <span className="truncate text-xs font-medium text-gray-500">
                        {crumb.label}
                      </span>
                    )}
                  </span>
                ))}
              </nav>
            ) : (
              <span className="text-xs font-medium text-gray-400">
                {pageTitle || t("adminNav.panelTitle")}
              </span>
            )}
          </div>
          {/* Right side: command palette trigger + language switcher + bell */}
          <div className="flex items-center gap-3">
            {/* Command palette trigger (TASK-4221) — before the language switch */}
            <CommandSearchInput />

            {/* Documentation launcher (TASK-4770) — any signed-in user gets the
                docs, superadmin included. Same component and control order as
                the dispatcher header. */}
            <DocsLink />

            {/* Language switcher — same behaviour as main app header */}
            <button
              type="button"
              onClick={handleToggleLang}
              aria-label="Switch language"
              className="rounded-lg border border-gray-300 px-2 py-0.5 text-xs font-semibold text-gray-600 transition-colors hover:bg-gray-50 hover:text-gray-900"
            >
              {langLabel}
            </button>

            {/* Superadmin system alerts bell (TASK-3620) */}
            <AlertsBell />
          </div>
        </header>

        {/* Page content */}
        {/* `relative` is load-bearing (BUG-484): it makes this scroll container
            the containing block for `sr-only` spans, which are `absolute` and
            would otherwise resolve against the initial containing block and
            inflate page height into a window-level scrollbar. Full rationale in
            AppShell.tsx. */}
        <main className="relative flex flex-1 flex-col overflow-y-auto bg-gray-50">
          <div className="flex-1 p-6">
            <ErrorBoundary
              key={pathname}
              fallbackRender={(error) => <AppErrorScreen error={error} />}
            >
              <Outlet />
            </ErrorBoundary>
          </div>
          <BrandFooter />
        </main>
      </div>
    </div>
  );
}
